import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Checkbox,
  CheckboxProps,
  Chip,
  Grid,
  Theme,
  Typography,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import _ from 'lodash';
import { isProgramApplyItem } from '../../containers/apply-provider/helper';
import { ApplyItem } from '../../containers/apply-provider/types';
import { ApplyModule } from '../../store/features/apply/types';
import { getDefaultImageSrc, loadDefaultImageOnError } from '../../utils/image';
import { formatToNepaliPrice, getFormattedDate } from '../../utils/text';
import { AutocompleteOption } from '../autocomplete-option/AutocompleteOption';
import { SelectOption } from '../controlled-autocomplete-input/types';
import { TextInput } from '../text-input/TextInput';
import { applyItemCardStyles } from './styles';

interface ApplyItemCardProps<ModuleType extends ApplyModule> extends Omit<CheckboxProps, 'sx'> {
  applyItem: ApplyItem;
  sx?: SystemStyleObject<Theme>;
  ProgramSelectFieldProps?: ModuleType extends 'scholarships'
    ? Partial<AutocompleteProps<SelectOption, false, true, false>>
    : never;
}
export const ApplyItemCard = <ModuleType extends ApplyModule>({
  applyItem,
  sx,
  ProgramSelectFieldProps,
  ...checkboxProps
}: ApplyItemCardProps<ModuleType>) => {
  const isProgram = isProgramApplyItem(applyItem);
  const getProgramSelectOptions = (): SelectOption[] => {
    if ('programs' in applyItem) {
      if (!applyItem.programs || applyItem.programs.length === 0) return [];
      return applyItem.programs.reduce((prevOptions, program) => {
        if (program.is_applicable && program.is_published && program.providers.is_published) {
          prevOptions.push({
            label: program.name,
            value: program.id.toString(),
            helperText: `${program.providers.name}, ${program.providers.address}`,
          });
        }
        return prevOptions;
      }, [] as SelectOption[]);
    }
    return [];
  };
  const programOptions = getProgramSelectOptions();

  const imageUrl = isProgram
    ? applyItem.providers?.media || getDefaultImageSrc('institute')
    : applyItem.media || getDefaultImageSrc('scholarship');
  const title = isProgram ? applyItem.name : applyItem.title;
  const instituteName = isProgram ? applyItem.providers?.name : applyItem.provider?.name;
  const address = isProgram ? applyItem.providers?.address : applyItem.provider?.address;
  const worth = isProgram ? undefined : applyItem.total_worth;

  return (
    <Box component="label" sx={_.compact([applyItemCardStyles.card, sx])}>
      <Box>
        <Grid container spacing={1} justifyContent="space-between" alignItems="center">
          <Grid item>
            <Box sx={{ bgcolor: 'grey.50', borderRadius: '0.5rem' }}>
              <Checkbox sx={{ borderRadius: 'inherit' }} {...checkboxProps} />
            </Box>
          </Grid>
          <Grid item>
            <Typography color="error" variant="small" fontWeight="Medium">
              Deadline: {getFormattedDate(applyItem.deadline)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Grid container alignItems="center" spacing={2.5}>
          <Grid item>
            <Box
              component="img"
              sx={applyItemCardStyles.img}
              src={imageUrl}
              onError={loadDefaultImageOnError.bind(null, isProgram ? 'institute' : 'scholarship')}
            />
          </Grid>
          <Grid item xs>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {title}
            </Typography>
            {instituteName && (
              <Box sx={{ mb: 0.25 }}>
                <Typography variant="medium" fontWeight="Medium" color="secondary">
                  {instituteName}
                </Typography>
              </Box>
            )}
            {address && (
              <Box sx={{ mb: 0.25 }}>
                <Typography variant="medium" color="secondary">
                  {address}
                </Typography>
              </Box>
            )}
            {Boolean(worth) && (
              <Box sx={{ mb: 0.25 }}>
                <Typography variant="medium" color="success" fontWeight="Bold">
                  {formatToNepaliPrice(parseFloat(worth as string))}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      {!isProgram && applyItem.programs && applyItem.programs.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Autocomplete
            {...ProgramSelectFieldProps}
            disableClearable
            options={programOptions}
            value={programOptions.find((option) => option.value === applyItem.selectedProgramId)}
            isOptionEqualToValue={(option, valueItem) => option.value.toString() === valueItem.value.toString()}
            renderInput={(params) => {
              return (
                <TextInput
                  sx={{ mb: 0 }}
                  FieldProps={{
                    ...params,
                  }}
                />
              );
            }}
            renderOption={(props, option) => {
              return (
                <AutocompleteOption {...props} label={option.label} helperText={option.helperText} key={option.value} />
              );
            }}
          />
        </Box>
      )}
      {isProgram && applyItem.discount && applyItem.discount.amount && applyItem.discount.percentage && (
        <Box sx={{ mt: 1 }}>
          <Chip
            variant="light"
            color="success"
            sx={applyItemCardStyles.discountBadge}
            label={`Scholarship: ${applyItem.discount.percentage}% ( ${formatToNepaliPrice(
              applyItem.discount.amount
            )} )`}
          />
        </Box>
      )}
    </Box>
  );
};
